import React, { useState } from "react"
import {
  VStack,
  Input,
  Text,
  Button,
  ModalFooter,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  Select
} from "@chakra-ui/react"
import { states } from "../utils/launchModalUtils"

interface AddBillingAddressProps {
  onSubmit: (billingAddress: any) => void
  onClose: () => void
}

export default function AddBillingAddress({ onSubmit, onClose }: AddBillingAddressProps) {
  const [billingAddress, setBillingAddress] = useState({
    address1: "",
    address2: null,
    city: "",
    state: "",
    zip: ""
  })

  const [errors, setErrors] = useState({
    address1: false,
    city: false,
    state: false,
    zip: false
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setBillingAddress((prevState) => ({ ...prevState, [name]: value }))
  }

  const handleSubmit = () => {
    const newErrors = {
      address1: billingAddress.address1 === "",
      city: billingAddress.city === "",
      state: billingAddress.state === "",
      zip: !/^\d{5,9}(-\d{4})?$/.test(billingAddress.zip)
    }

    setErrors(newErrors)

    if (!Object.values(newErrors).some((error) => error)) {
      onSubmit(billingAddress)
    }
  }

  return (
    <>
      <ModalHeader>Billing address for international segments</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text mb={4}>Please enter a U.S. billing address which will apply to all international segments.</Text>
        <VStack spacing={4}>
          <VStack align="start" width="100%">
            <Text as="b">
              Address Line 1{" "}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <Input
              type="text"
              name="address1"
              value={billingAddress.address1}
              onChange={handleChange}
              isInvalid={errors.address1}
            />
            {errors.address1 && (
              <Text color="red.500" fontSize="sm">
                Address Line 1 is required.
              </Text>
            )}
          </VStack>

          <VStack align="start" width="100%">
            <Text as="b">Address Line 2</Text>
            <Input type="text" name="address2" value={billingAddress.address2} onChange={handleChange} />
          </VStack>

          <VStack align="start" width="100%">
            <Text as="b">
              City{" "}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <Input
              type="text"
              name="city"
              value={billingAddress.city}
              onChange={handleChange}
              isInvalid={errors.city}
            />
            {errors.city && (
              <Text color="red.500" fontSize="sm">
                City is required.
              </Text>
            )}
          </VStack>

          <VStack align="start" width="100%">
            <Text as="b">
              State{" "}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <Select name="state" value={billingAddress.state} onChange={handleChange} isInvalid={errors.state}>
              <option value="" label="Select state" />
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
            {errors.state && (
              <Text color="red.500" fontSize="sm">
                State is required.
              </Text>
            )}
          </VStack>

          <VStack align="start" width="100%">
            <Text as="b">
              Zip Code{" "}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <Input type="text" name="zip" value={billingAddress.zip} onChange={handleChange} isInvalid={errors.zip} />
            {errors.zip && (
              <Text color="red.500" fontSize="sm">
                Zip Code is required and must be 5-9 digits.
              </Text>
            )}
          </VStack>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button mr={3} onClick={onClose}>
          Cancel
        </Button>
        <Button colorScheme="carrotOrange" onClick={handleSubmit}>
          Next
        </Button>
      </ModalFooter>
    </>
  )
}
